import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, Heading, Text, VStack, HStack, Card, CardBody, SimpleGrid, Stat, StatLabel, StatNumber,
  useToast, Container, Badge, Alert, AlertIcon, Tabs, TabList, TabPanels, Tab, TabPanel
} from '@chakra-ui/react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import { FiAlertTriangle, FiClock } from 'react-icons/fi';
import Page from '../../components/ui/Page';
import SectionCard from '../../components/ui/SectionCard';
import LoadingState from '../../components/ui/LoadingState';
import ErrorState from '../../components/ui/ErrorState';
import useAnalyticsAPI from '../../hooks/useAnalyticsAPI';

const CHART_COLORS = [
  '#3182CE', '#38A169', '#D69E2E', '#E53E3E', '#805AD5', 
  '#319795', '#DD6B20', '#9F7AEA', '#2B6CB0', '#38A169'
];

export default function AnalysisTrendsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const { makeRequest } = useAnalyticsAPI();
  const toast = useToast();

  useEffect(() => {
    const fetchTrendsData = async () => {
      try {
    setLoading(true);
    setError(null);
        
        const result = await makeRequest('/api/bugs/analytics/trends-dashboard', {});
        
        console.log('API Response:', result);
        
        if (result.status === 'success' && result.data) {
          setData(result.data);
          toast({
            title: "Trends Dashboard Loaded",
            description: "Comprehensive trends analysis data loaded successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } else {
          throw new Error('Failed to load trends data');
        }
      } catch (err) {
        console.error('Failed to fetch trends data:', err);
        setError(err.message || 'Failed to load trends dashboard');
        toast({
          title: "Error",
          description: err.message || "Failed to load trends dashboard",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTrendsData();
  }, [makeRequest, toast]);

  // Process data for charts
  const processedData = useMemo(() => {
    if (!data) return {};

    console.log('Processing trends data:', data);
    console.log('Individual metrics:', data.individualMetrics);

    return {
      // Time trends data
      timeTrends: data.timeTrends?.monthlyTrends?.map(item => ({
        month: item.month,
        total: item.count,
        critical: item.severity?.CRITICAL || 0,
        high: item.severity?.HIGH || 0,
        medium: item.severity?.MEDIUM || 0,
        low: item.severity?.LOW || 0
      })) || [],

      // Root cause distribution
      rootCauses: data.rootCauseAnalysis?.distribution?.map(item => ({
        name: item.category,
        value: parseInt(item.count),
        percentage: parseFloat(item.percentage)
      })) || [],

      // Severity distribution - ensure we have valid data
      severityDistribution: Object.entries(data.rootCauseAnalysis?.severityDistribution || {}).map(([severity, count]) => ({
        name: severity,
        value: parseInt(count) || 0
      })).filter(item => item.value > 0),

      // Category trends over time
      categoryTrends: data.rootCauseAnalysis?.categoryTrends?.map(month => ({
        month: month.month,
        ...month.categories
      })) || [],

      // Top keywords - ensure we have valid data
      topKeywords: (data.rootCauseAnalysis?.topKeywords || []).slice(0, 10).map(item => ({
        word: item.word || 'Unknown',
        count: parseInt(item.count) || 0
      })).filter(item => item.count > 0).sort((a, b) => b.count - a.count),

      // Recurring bugs - ensure we have valid data
      recurringBugs: (data.individualMetrics?.recurringBugs || []).slice(0, 10).map(item => {
        const originalName = item.name || 'Unknown';
        // Format the name: remove prefix and convert to readable format
        let formattedName = originalName;
        
        // Remove prefixes like "DATA_CALCULATION:", "INTEGRATION:", etc.
        if (formattedName.includes(':')) {
          formattedName = formattedName.split(':')[1];
        }
        
        // Convert underscores to spaces and capitalize words
        formattedName = formattedName
          .replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        return {
          name: formattedName,
          originalName: originalName, // Keep original for tooltip
          count: parseInt(item.count) || 0
        };
      }).filter(item => item.count > 0).sort((a, b) => b.count - a.count),

      // Client impact
      clientImpact: (data.clientImpact?.severityDistribution || []).map(item => ({
        severity: item.severity,
        count: item.count,
        percentage: parseFloat(item.percentage)
      })),

      // Alternative severity distribution from client impact
      clientSeverityDistribution: (data.clientImpact?.severityDistribution || []).map(item => ({
        name: item.severity,
        value: parseInt(item.count) || 0
      })).filter(item => item.value > 0)
    };
  }, [data]);

  // Debug logging
  console.log('Processed data:', processedData);
  console.log('Top keywords:', processedData.topKeywords);
  console.log('Recurring bugs:', processedData.recurringBugs);

  if (loading) return <LoadingState title="Loading Trends Dashboard" minH="70vh" />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <ErrorState message="No data available" />;

  return (
    <Page maxW="full">
      <Container maxW="8xl" p={0}>
        {/* Header */}
        <Box mb={8}>
          <Heading size="xl" color="gray.600" mb={2}>Trends Dashboard</Heading>
          <Text fontSize="lg" color="gray.500">
            Comprehensive analysis of bug trends, patterns, and insights
          </Text>
        </Box>

        {/* Summary Stats */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Issues Analyzed</StatLabel>
                <StatNumber color="blue.500">{data.processingInfo?.totalAnalyzed || 0}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Unique Clients</StatLabel>
                <StatNumber color="green.500">{data.clientImpact?.clientImpactSummary?.uniqueClientCount || 0}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Affected Employees</StatLabel>
                <StatNumber color="orange.500">{data.clientImpact?.clientImpactSummary?.affectedEmployeeCount || 0}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Risk Level</StatLabel>
                <StatNumber color={data.aiInsights?.riskAssessment?.overallRiskLevel === 'HIGH' ? 'red.500' : 'green.500'}>
                  {data.aiInsights?.riskAssessment?.overallRiskLevel || 'UNKNOWN'}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Main Dashboard Tabs */}
        <Tabs variant="enclosed" colorScheme="blue" mb={6}>
          <TabList>
            <Tab>Time Trends</Tab>
            <Tab>Root Causes</Tab>
            <Tab>Severity Analysis</Tab>
            <Tab>Client Impact</Tab>
            <Tab>AI Insights</Tab>
          </TabList>

          <TabPanels>
            {/* Time Trends Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                {/* Monthly Trends Chart */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Monthly Issue Trends</Heading>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={processedData.timeTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="total" stackId="1" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                        <Bar dataKey="critical" stackId="2" fill="#E53E3E" />
                        <Bar dataKey="high" stackId="2" fill="#DD6B20" />
                        <Bar dataKey="medium" stackId="2" fill="#D69E2E" />
                        <Bar dataKey="low" stackId="2" fill="#38A169" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </VStack>
                </SectionCard>

                {/* Category Trends Over Time */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Root Cause Trends by Month</Heading>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={processedData.categoryTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="DATA_CALCULATION" stroke="#3182CE" strokeWidth={2} />
                        <Line type="monotone" dataKey="WORKFLOW_PROCESS" stroke="#38A169" strokeWidth={2} />
                        <Line type="monotone" dataKey="DATE_TIME_LOGIC" stroke="#D69E2E" strokeWidth={2} />
                        <Line type="monotone" dataKey="POLICY_CONFIGURATION" stroke="#E53E3E" strokeWidth={2} />
                        <Line type="monotone" dataKey="PERMISSION_ACCESS" stroke="#805AD5" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </VStack>
                </SectionCard>
              </VStack>
            </TabPanel>

            {/* Root Causes Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                {/* Root Cause Distribution */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Root Cause Distribution</Heading>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={processedData.rootCauses}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${name}: ${percentage}%`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {processedData.rootCauses.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </VStack>
                </SectionCard>

                {/* Top Keywords */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Most Common Keywords</Heading>
                    {processedData.topKeywords.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={400}>
                                                      <BarChart 
                              data={processedData.topKeywords} 
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                              <XAxis 
                                dataKey="word"
                                tick={{ fontSize: 12, fill: '#4A5568' }}
                                axisLine={{ stroke: '#CBD5E0' }}
                                tickLine={{ stroke: '#CBD5E0' }}
                              />
                              <YAxis 
                                domain={[0, 'dataMax']}
                                tick={{ fontSize: 12, fill: '#4A5568' }}
                                axisLine={{ stroke: '#CBD5E0' }}
                                tickLine={{ stroke: '#CBD5E0' }}
                              />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E2E8F0',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                }}
                                labelStyle={{ fontWeight: 'bold', color: '#2D3748' }}
                              />
                              <Bar 
                                dataKey="count" 
                                fill="#3182CE"
                                name="Occurrences"
                              />
                              <Legend />
                            </BarChart>
                        </ResponsiveContainer>
                      </>
                    ) : (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">No keyword data available</Text>
                        <Text fontSize="sm" color="gray.400">Data points: {processedData.topKeywords.length}</Text>
                      </Box>
                    )}
                  </VStack>
                </SectionCard>

                {/* Recurring Bugs */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Recurring Bug Patterns</Heading>
                    {processedData.recurringBugs.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={400}>
                                                      <BarChart 
                              data={processedData.recurringBugs} 
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                              <XAxis 
                                dataKey="name"
                                tick={{ fontSize: 10, fill: '#4A5568' }}
                                axisLine={{ stroke: '#CBD5E0' }}
                                tickLine={{ stroke: '#CBD5E0' }}
                                angle={-45}
                                textAnchor="end"
                                height={80}
                              />
                              <YAxis 
                                domain={[0, 'dataMax']}
                                tick={{ fontSize: 12, fill: '#4A5568' }}
                                axisLine={{ stroke: '#CBD5E0' }}
                                tickLine={{ stroke: '#CBD5E0' }}
                              />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E2E8F0',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                }}
                                labelStyle={{ fontWeight: 'bold', color: '#2D3748' }}
                                formatter={(value, name, props) => {
                                  const originalName = props.payload.originalName;
                                  return [`${value} occurrences`, originalName];
                                }}
                              />
                              <Bar 
                                dataKey="count" 
                                fill="#E53E3E"
                                name="Occurrences"
                              />
                              <Legend />
                            </BarChart>
                        </ResponsiveContainer>
                      </>
                    ) : (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">No recurring bug data available</Text>
                        <Text fontSize="sm" color="gray.400">Data points: {processedData.recurringBugs.length}</Text>
                      </Box>
                    )}
                  </VStack>
                </SectionCard>
              </VStack>
            </TabPanel>

            {/* Severity Analysis Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                {/* Severity Distribution */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Severity Distribution</Heading>
                    {(processedData.severityDistribution.length > 0 || processedData.clientSeverityDistribution.length > 0) ? (
                      <>
                        <ResponsiveContainer width="100%" height={400}>
                          <PieChart>
                            <Pie
                              data={processedData.severityDistribution.length > 0 ? processedData.severityDistribution : processedData.clientSeverityDistribution}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, value }) => `${name}: ${value}`}
                              outerRadius={120}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {(processedData.severityDistribution.length > 0 ? processedData.severityDistribution : processedData.clientSeverityDistribution).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </>
                    ) : (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">No severity distribution data available</Text>
                      </Box>
                    )}
                  </VStack>
                </SectionCard>

                {/* Client Impact Severity */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Client Impact by Severity</Heading>
                    {processedData.clientImpact.length > 0 ? (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={processedData.clientImpact} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis 
                            dataKey="severity" 
                            tick={{ fontSize: 12, fill: '#4A5568' }}
                            axisLine={{ stroke: '#CBD5E0' }}
                            tickLine={{ stroke: '#CBD5E0' }}
                          />
                          <YAxis 
                            domain={[0, 'dataMax']}
                            tick={{ fontSize: 12, fill: '#4A5568' }}
                            axisLine={{ stroke: '#CBD5E0' }}
                            tickLine={{ stroke: '#CBD5E0' }}
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E2E8F0',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}
                            labelStyle={{ fontWeight: 'bold', color: '#2D3748' }}
                            formatter={(value, name) => [`${value} issues`, name]}
                          />
                          <Bar 
                            dataKey="count" 
                            fill="#805AD5"
                            name="Issues"
                          />
                          <Legend />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">No client impact severity data available</Text>
                      </Box>
                    )}
                  </VStack>
                </SectionCard>
              </VStack>
            </TabPanel>

            {/* Client Impact Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                {/* Client Impact Summary */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Client Impact Summary</Heading>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="100%">
                      <Box>
                        <Text fontSize="sm" color="gray.600">Total Unique Clients</Text>
                        <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                          {data.clientImpact?.clientImpactSummary?.uniqueClientCount || 0}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600">Affected Employees</Text>
                        <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                          {data.clientImpact?.clientImpactSummary?.affectedEmployeeCount || 0}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600">Reported Users</Text>
                        <Text fontSize="2xl" fontWeight="bold" color="green.500">
                          {data.clientImpact?.clientImpactSummary?.totalReportedUserCount || 0}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </VStack>
                </SectionCard>

                {/* Impact Type Distribution */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Impact Type Distribution</Heading>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={Object.entries(data.clientImpact?.clientImpactSummary?.impactTypeDistribution || {}).map(([type, count]) => ({
                            name: type,
                            value: count
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {Object.entries(data.clientImpact?.clientImpactSummary?.impactTypeDistribution || {}).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </VStack>
                </SectionCard>
              </VStack>
            </TabPanel>

            {/* AI Insights Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                {/* Executive Summary */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">AI Executive Summary</Heading>
                    <Alert status="info" variant="left-accent">
                      <AlertIcon />
                      <Text fontSize="sm">
                        {data.aiInsights?.trends?.executiveSummary || 'No executive summary available'}
                      </Text>
                    </Alert>
                  </VStack>
                </SectionCard>

                {/* Risk Assessment */}
                <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Risk Assessment</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                      <Box>
                        <Text fontSize="sm" color="gray.600">Overall Risk Level</Text>
                        <Badge 
                          colorScheme={data.aiInsights?.riskAssessment?.overallRiskLevel === 'HIGH' ? 'red' : 'green'} 
                          fontSize="lg" 
                          p={2}
                        >
                          {data.aiInsights?.riskAssessment?.overallRiskLevel || 'UNKNOWN'}
                        </Badge>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600">Risk Score</Text>
                        <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                          {data.aiInsights?.riskAssessment?.riskScore || 'N/A'}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </VStack>
                </SectionCard>

                {/* Recommended Actions */}
      <SectionCard>
                  <VStack align="start" spacing={4}>
                    <Heading size="md">Immediate Actions</Heading>
                    <VStack align="start" spacing={2} w="100%">
                      {data.aiInsights?.recommendations?.immediate?.priority1?.map((action, index) => (
                        <Box key={index} p={3} bg="red.50" borderRadius="md" w="100%">
                          <HStack>
                            <FiAlertTriangle color="red" />
                            <Text fontSize="sm">{action}</Text>
                          </HStack>
                        </Box>
                      ))}
                      {data.aiInsights?.recommendations?.immediate?.priority2?.map((action, index) => (
                        <Box key={index} p={3} bg="orange.50" borderRadius="md" w="100%">
                          <HStack>
                            <FiClock color="orange" />
                            <Text fontSize="sm">{action}</Text>
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  </VStack>
      </SectionCard>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>
    </Page>
  );
}


